// Guided: create a Processing Rule (the private transformation/validation logic
// of the processing_rules strategy). Domain-agnostic. Drives the assistant to
// author a rule with the correct engine envelope and attach it to an element.
// Gates on the project's processing_rule count rising.
//
// NOTE: processing rules are read via the chat/MCP (query_processing_rules); the
// client has no direct REST count, so this guide gates on the assistant's own
// confirmation by polling a lightweight DOM signal is unreliable — instead we
// drive the chat and let the agent report, advancing on a timed completion of the
// chat turn rather than a client-side row read.

export const processingRuleGuide = {
  key: "processing_rule",
  title: "Cortex Guided · Add a processing rule",
  makeSteps(engine, ctx) {
    const { setStatus, chatSend, waitChatIdle, goPage, refreshPages } = engine;
    return [
      {
        title: "Add a processing rule",
        lines: [
          "A <b>processing rule</b> is column-level transform/validation logic —",
          "e.g. normalize a number, trim text, validate a code against a list.",
          "Rules attach to data elements and run inside the pipeline. Press Next.",
        ],
        run: async () => { goPage("/processing-rules"); setStatus("Ready. Press Next, then describe the rule."); },
      },
      {
        title: "1 · Describe the rule",
        lines: [
          "Tell the assistant the transform you want, in plain language —",
          "<i>“strip currency symbols and commas, keep a number”</i> or",
          "<i>“uppercase the country code”</i>. It writes the Python + envelope.",
        ],
        run: async () => {
          goPage("/processing-rules");
          await chatSend(
            "Help me create a new PROCESSING RULE in this project. Ask me nothing up front — from my description author a Python rule with create_processing_rule whose code returns the engine envelope {isValid, transformedValue, message}. If I haven't described one yet, create a clean reusable `trim_whitespace` rule as a starting example. Report the rule name + code when done, and tell me I can attach it to a data element next.",
          );
          await waitChatIdle(240_000);
          refreshPages();
          setStatus("✓ rule authored — see the chat for the code; Next to attach it");
        },
        autoAdvanceReady: () => false,
      },
      {
        title: "2 · Attach to an element (optional)",
        lines: [
          "Rules run when attached to a data element (attach_rule_to_element).",
          "Ask the assistant to attach this rule to the element you want it on,",
          "or skip — it stays in the library for later.",
        ],
        run: async () => {
          goPage("/processing-rules");
          await chatSend(
            "If I name an element to attach the rule to, call attach_rule_to_element for it; otherwise just list the rules in this project with query_processing_rules so I can see it's registered. Don't ask for confirmation.",
          );
          await waitChatIdle(180_000);
          refreshPages();
          setStatus("✓ done — rule is in the project's rule library");
        },
      },
    ];
  },
};
